import { useCallback, useEffect, useState, type FormEvent } from 'react';
import type { ProjectSummary } from '@todo/shared';
import { ApiError } from '../api/client';
import { projectApi } from '../api/project.api';

/**
 * Projects (Manage) - shaped after DepartmentsPage.
 *
 * A project is a name plus a description that tasks and sub-tasks get tagged
 * with. Create, edit in place, delete with a confirm step. Deleting is a
 * SOFT delete on the server - tagged tasks keep their history; their project
 * simply reads as none from then on, which the row's task count lets you
 * judge before you press the button.
 */
export default function ProjectsPage() {
  const [projects, setProjects] = useState<ProjectSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [flash, setFlash] = useState<string | null>(null);
  const [newName, setNewName] = useState('');
  const [newDescription, setNewDescription] = useState('');
  const [createBusy, setCreateBusy] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setProjects(await projectApi.list());
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not load projects');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function create(event: FormEvent) {
    event.preventDefault();
    const name = newName.trim();
    if (!name) return;
    setCreateBusy(true);
    try {
      const description = newDescription.trim();
      await projectApi.create({ name, ...(description ? { description } : {}) });
      setNewName('');
      setNewDescription('');
      setFlash(`${name} created.`);
      void load();
    } catch (err) {
      setFlash(err instanceof ApiError ? err.message : 'Could not create the project');
    } finally {
      setCreateBusy(false);
    }
  }

  async function save(project: ProjectSummary) {
    const name = editName.trim();
    const description = editDescription.trim() || null;
    if (!name) {
      setEditingId(null);
      return;
    }
    if (name === project.name && description === project.description) {
      setEditingId(null);
      return;
    }
    setBusyId(project.id);
    try {
      await projectApi.update(project.id, { name, description });
      setProjects((current) =>
        current
          .map((p) => (p.id === project.id ? { ...p, name, description } : p))
          .sort((a, b) => a.name.localeCompare(b.name)),
      );
      setFlash(`${name} saved.`);
      setEditingId(null);
    } catch (err) {
      setFlash(err instanceof ApiError ? err.message : 'Could not save the project');
    } finally {
      setBusyId(null);
    }
  }

  async function remove(project: ProjectSummary) {
    setBusyId(project.id);
    try {
      await projectApi.remove(project.id);
      setProjects((current) => current.filter((p) => p.id !== project.id));
      setFlash(`${project.name} deleted. Tagged tasks keep their history.`);
    } catch (err) {
      setFlash(err instanceof ApiError ? err.message : 'Could not delete the project');
    } finally {
      setBusyId(null);
      setConfirmDeleteId(null);
    }
  }

  return (
    <div className="mx-auto max-w-4xl px-8 py-8">
      <header>
        <h1 className="text-xl font-semibold text-slate-900">Projects</h1>
        <p className="mt-1 text-sm text-slate-500">
          {loading ? 'Loading...' : `${projects.length} ${projects.length === 1 ? 'project' : 'projects'}`}
        </p>
      </header>

      {flash && (
        <div role="status" className="mt-5 flex items-start justify-between gap-3 rounded-lg bg-emerald-50 px-3.5 py-2.5 text-sm text-emerald-800 ring-1 ring-emerald-200">
          <span>{flash}</span>
          <button type="button" onClick={() => setFlash(null)} aria-label="Dismiss"
                  className="text-emerald-600 hover:text-emerald-800">&times;</button>
        </div>
      )}

      <form onSubmit={(e) => void create(e)} className="mt-5 space-y-2">
        <div className="flex gap-2">
          <input
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            maxLength={120}
            placeholder="New project name"
            className="min-w-0 flex-1 rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none transition placeholder:text-slate-400 focus:border-brand-500 focus:ring-2 focus:ring-brand-100"
          />
          <button type="submit" disabled={createBusy || !newName.trim()}
            className="rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white transition hover:bg-brand-700 disabled:opacity-50">
            {createBusy ? 'Creating...' : 'Add project'}
          </button>
        </div>
        <textarea
          value={newDescription}
          onChange={(e) => setNewDescription(e.target.value)}
          maxLength={1000}
          rows={2}
          placeholder="What this project is about (optional)"
          className="w-full resize-y rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none transition placeholder:text-slate-400 focus:border-brand-500 focus:ring-2 focus:ring-brand-100"
        />
      </form>

      <div className="mt-5 overflow-hidden rounded-xl border border-slate-200 bg-white">
        {error ? (
          <div className="px-5 py-12 text-center">
            <p className="text-sm text-red-600">{error}</p>
            <button type="button" onClick={() => void load()}
              className="mt-3 rounded-lg border border-slate-300 px-3 py-1.5 text-sm text-slate-700 transition hover:bg-slate-50">
              Try again
            </button>
          </div>
        ) : loading ? (
          <ul className="divide-y divide-slate-100">
            {[0, 1, 2].map((i) => (
              <li key={i} className="px-5 py-4">
                <div className="h-3 w-48 animate-pulse rounded bg-slate-100" />
              </li>
            ))}
          </ul>
        ) : projects.length === 0 ? (
          <div className="px-5 py-14 text-center">
            <p className="text-sm font-medium text-slate-900">No projects yet</p>
            <p className="mt-1 text-sm text-slate-500">
              Create the first one above - it will appear in the task form's Project picker.
            </p>
          </div>
        ) : (
          <table className="w-full text-left text-sm">
            <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-5 py-2.5 font-medium">Name</th>
                <th className="px-5 py-2.5 font-medium">Description</th>
                <th className="px-5 py-2.5 text-right font-medium">Tasks</th>
                <th className="px-5 py-2.5 text-right font-medium">Not started</th>
                <th className="px-5 py-2.5 text-right font-medium">In progress</th>
                <th className="px-5 py-2.5 font-medium"><span className="sr-only">Actions</span></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {projects.map((project) => (
                <tr key={project.id} className="align-top transition hover:bg-slate-50/70">
                  <td className="px-5 py-3">
                    {editingId === project.id ? (
                      <input
                        autoFocus
                        value={editName}
                        onChange={(e) => setEditName(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') { e.preventDefault(); void save(project); }
                          if (e.key === 'Escape') setEditingId(null);
                        }}
                        maxLength={120}
                        className="w-full max-w-xs rounded-lg border border-brand-400 px-2.5 py-1.5 text-sm outline-none ring-2 ring-brand-100"
                      />
                    ) : (
                      <span className="font-medium text-slate-900">{project.name}</span>
                    )}
                  </td>
                  <td className="px-5 py-3 text-slate-600">
                    {editingId === project.id ? (
                      <textarea
                        value={editDescription}
                        onChange={(e) => setEditDescription(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Escape') setEditingId(null); }}
                        maxLength={1000}
                        rows={2}
                        placeholder="Description (optional)"
                        className="w-full resize-y rounded-lg border border-brand-400 px-2.5 py-1.5 text-sm outline-none ring-2 ring-brand-100"
                      />
                    ) : (
                      <span className="line-clamp-2 text-slate-500">{project.description ?? '—'}</span>
                    )}
                  </td>
                  <td className="px-5 py-3 text-right tabular-nums text-slate-600">
                    {project.taskCount}
                  </td>
                  {/* The two that decide whether the project can go. A zero
                      is greyed rather than coloured: nothing to act on. */}
                  <td className={`px-5 py-3 text-right tabular-nums ${
                    project.notStartedCount > 0 ? 'font-medium text-amber-600' : 'text-slate-400'
                  }`}>
                    {project.notStartedCount}
                  </td>
                  <td className={`px-5 py-3 text-right tabular-nums ${
                    project.inProgressCount > 0 ? 'font-medium text-sky-600' : 'text-slate-400'
                  }`}>
                    {project.inProgressCount}
                  </td>
                  <td className="px-5 py-3 text-right">
                    {editingId === project.id ? (
                      <span className="inline-flex items-center gap-1.5 whitespace-nowrap">
                        <button type="button" disabled={busyId === project.id}
                          onClick={() => void save(project)}
                          className="rounded-lg bg-brand-600 px-2.5 py-1 text-xs font-medium text-white transition hover:bg-brand-700 disabled:opacity-50">
                          Save
                        </button>
                        <button type="button" onClick={() => setEditingId(null)}
                          className="rounded-lg px-2 py-1 text-xs font-medium text-slate-600 transition hover:bg-slate-100">
                          Cancel
                        </button>
                      </span>
                    ) : confirmDeleteId === project.id ? (
                      <span className="inline-flex items-center gap-1.5 whitespace-nowrap">
                        <button type="button" disabled={busyId === project.id}
                          onClick={() => void remove(project)}
                          className="rounded-lg bg-red-600 px-2.5 py-1 text-xs font-medium text-white transition hover:bg-red-700 disabled:opacity-50">
                          {busyId === project.id ? 'Deleting...' : 'Confirm'}
                        </button>
                        <button type="button" onClick={() => setConfirmDeleteId(null)}
                          className="rounded-lg px-2 py-1 text-xs font-medium text-slate-600 transition hover:bg-slate-100">
                          Keep
                        </button>
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 whitespace-nowrap">
                        <button type="button"
                          onClick={() => {
                            setEditingId(project.id);
                            setEditName(project.name);
                            setEditDescription(project.description ?? '');
                          }}
                          className="rounded-lg px-2.5 py-1 text-xs font-medium text-slate-600 transition hover:bg-slate-100 hover:text-slate-900">
                          Edit
                        </button>
                        {/* Disabled while the project still has unfinished
                            work. The API refuses it anyway - this just says
                            so before the click rather than after. */}
                        <button
                          type="button"
                          disabled={project.activeTaskCount > 0}
                          onClick={() => setConfirmDeleteId(project.id)}
                          title={project.activeTaskCount > 0
                            ? `${project.activeTaskCount} active task${project.activeTaskCount === 1 ? '' : 's'} (not started or in progress) - complete, put on hold, or move them first`
                            : undefined}
                          className="rounded-lg px-2.5 py-1 text-xs font-medium text-red-600 transition hover:bg-red-50 disabled:cursor-not-allowed disabled:text-slate-400 disabled:hover:bg-transparent">
                          Delete
                        </button>
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
